import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import HistoryIcon from "@mui/icons-material/History";
import SearchIcon from "@mui/icons-material/Search";
import { useLocation, useNavigate } from "react-router-dom";
import { TableLoader } from "../../components/TableLoader";
import { TableNodata } from "../../components/TableNodata";
import api from "../../api/client";
import ManageSearchOutlinedIcon from "@mui/icons-material/ManageSearchOutlined";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import CustomPagination from "../../components/CustomPagination";
import { useSnackbar } from "../../providers/SnackbarProvider";
import useDebounce from "../../hooks/useDebounce";
import { useChangeableItems, getLabelByKey } from "../../store/useDynamicLabel";

const StudentsResultList = () => {

  const changeableItems = useChangeableItems();
  const getLabel = (itemKey) => getLabelByKey(changeableItems, itemKey);

  // Cache for table data (NO re-render)
  const tableCacheRef = useRef({});
  const navigate = useNavigate();
  const location = useLocation();
  const {
    category,
    level,
    testType,
    institute_Name,
    institute_id,
    testId,
    studentName,
    studentid,
    assessmentId
  } = location.state || "";
  const { showSnackbar } = useSnackbar();
  const [showTable, setShowTable] = useState(false);
  const [page, setPage] = useState(+localStorage.getItem("currentPage") || 0);
  const [rowsPerPage, setRowsPerPage] = useState(
    +localStorage.getItem("rowsPerPage") || 10
  );
  const [searchKey, setSearchKey] = useState(
    localStorage.getItem("searchvalue") || ""
  );
  const debouncedSearch = useDebounce(searchKey)
  const [isViewAssessment, setIsViewAssessment] = useState(
    location.state?.viewAssessments === true
  );
  const [listData, setListData] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [testTypes, setTestTypes] = useState([]);
  const [open, setOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [selectedTestType, setSelectedTestType] = useState(null);
  const [selectedTestTypeId, setSelectedTestTypeId] = useState(
    testId
      ? testId
      : testType === "Self Assessment"
        ? 1
        : testType === "Proctored Assessment"
          ? 2
          : +localStorage?.getItem("applyTestTypeId")
  );
  const [selectedCollege, setSelectedCollege] = useState(
    institute_id ? institute_id : +localStorage.getItem("InstituteId") || null
  );
  const [selectedCollegeName, setSelectedCollegeName] = useState("");
  const [colleges, setColleges] = useState([]);
  const [studentsList, setStudentsList] = useState([]);

  const [selectedStudent, setSelectedStudent] = useState(
    localStorage.getItem("applyStudentId") !== "null" ? localStorage.getItem("applyStudentId") || null : null
  );
  const [selectedStudentName, setSelectedStudentName] = useState(
    localStorage.getItem("applyStudent") !== "null" ? localStorage.getItem("applyStudent") || "" : ""
  );

  // Applied filters (shown as chips after clicking "Apply")
  const [appliedCategory, setAppliedCategory] = useState(
    category
      ? category
      : localStorage.getItem("applyCategory") !== "null"
        ? localStorage.getItem("applyCategory")
        : null || null
  );
  const [appliedLevel, setAppliedLevel] = useState(
    level
      ? level
      : localStorage.getItem("applyLevel") !== "null"
        ? localStorage.getItem("applyLevel")
        : null || null
  );
  const [appliedTestType, setAppliedTestType] = useState(
    localStorage.getItem("applyTestType") || testType || null
  );
  console.log(appliedTestType, appliedLevel, appliedCategory);

  const [appliedTestTypeId, setAppliedTestTypeId] = useState(
    testId
      ? testId
      : testType
        ? testType
        : +localStorage?.getItem("applyTestTypeId") || null
  );

  const [appliedInstituteName, setAppliedInstituteName] = useState(
    localStorage.getItem("applyInstitute") || institute_Name || null
  );

  const [appliedInstituteId, setAppliedInstituteId] = useState(
    institute_id ? institute_id : null
  );

  const [appliedStudentId, setAppliedStudentId] = useState(
    localStorage.getItem("applyStudentId") !== "null" ? localStorage.getItem("applyStudentId") || null : null
  );
  const [appliedStudentName, setAppliedStudentName] = useState(
    localStorage.getItem("applyStudent") !== "null" ? localStorage.getItem("applyStudent") || "" : ""
  );

  const levelOptions = ["Beginner", "Intermediate", "Advanced"];

  const headers = [
    { label: "S.No", align: "center", key: "sno" },
    { label: `${getLabel('STUDENT')}`, align: "left", key: "name" },
    ...(localStorage.getItem("role") === "SUPER_ADMIN"
      ? [
        {
          label: "Institute",
          align: "left",
          key: "displayname",
        },
      ]
      : []),
    { label: getLabel('SUBJECT'), align: "left", key: "category" },
    { label: "Assessment Type / Assessment ID", align: "left", key: "level" },
    { label: "Total Marks", align: "center", key: "total_marks" },
    { label: "Submitted Date", align: "left", key: "Submitted Date & Time" },
    { label: "Actions", align: "center", key: "actions" },
  ];

  // ===== Fetch API Data =====
  const fetchData = async () => {
    setLoading(true);

    // 🔑 Unique key for cache
    const cacheKey = `page_${page}_size_${rowsPerPage}_search_${debouncedSearch}_cat_${appliedCategory}_level_${appliedLevel}_test_${appliedTestTypeId}_inst_${appliedInstituteId}_student_${appliedStudentId}_assessment_${assessmentId}`;

    // ✅ If cached → use it
    if (tableCacheRef.current[cacheKey]) {
      const cached = tableCacheRef.current[cacheKey];
      setListData(cached.data);
      setCount(cached.total);
      setLoading(false);
      return;
    }

    try {
      const response = await api.post("/ExamHistoryDetails", {
        page: page,
        size: rowsPerPage,
        search_key: isViewAssessment ? null : debouncedSearch || null,
        category: assessmentId ? null : appliedCategory || null,
        level: appliedLevel || null,
        test_type:
          appliedTestTypeId === "Self Assessment"
            ? 1
            : appliedTestTypeId === "Proctored Assessment"
              ? 2
              : appliedTestTypeId || null,
        institute_id: appliedInstituteId || null,
        student_id: appliedStudentId || studentid || null,
        assessment_id: assessmentId || null,
      });

      if (response.data.status === "success") {
        const data = response.data.data;
        const total = response.data.total;

        // ✅ Store in cache (NO re-render)
        tableCacheRef.current[cacheKey] = {
          data,
          total,
        };

        // ✅ Update UI state
        setListData(data);
        setCount(total);
      } else {
        setListData([]);
        setCount(0);
      }
    } catch (error) {
      console.error("API Error:", error);
      setListData([]);
      setCount(0);
    } finally {
      setLoading(false);
    }
  };

  const categoryDatas = async () => {
    try {
      const res = await api.post("ListCategoryNames");
      setCategories(res?.data?.categories);
    } catch (error) {
      console.log("Error :", error);
    }
  };

  const instituteList = async () => {
    try {
      const res = await api.post("/ListInstituteNames");
      if (res?.data?.status === "success") {
        setColleges(res?.data?.data);
      } else {
        showSnackbar("error", res?.data?.message);
      }
    } catch (error) {
      console.log("Error :", error);
      showSnackbar("error", "Failed to fetch institutes.");
    }
  };

  const studentsListApi = async () => {
    try {
      const res = await api.post("/ListofStudentexam");
      if (res?.data?.status === "success") {
        setStudentsList(res?.data?.data || []);
      } else {
        showSnackbar("error", res?.data?.message);
      }
    } catch (error) {
      console.log("Error :", error);
      showSnackbar("error", "Failed to fetch students.");
    }
  };

  const testtypeList = async () => {
    try {
      const res = await api.post("/ListAssessmentTypes");
      if (res?.data?.status === "success") {
        setTestTypes(res?.data?.data || []);
      } else {
        showSnackbar(
          "error",
          res?.data?.message || "Failed to fetch assessment types!"
        );
      }
    } catch (error) {
      showSnackbar(
        "error",
        "Something went wrong while fetching assessment types!"
      );
    }
  };

  useEffect(() => {
    fetchData();
    categoryDatas();
    instituteList();
    testtypeList();
    if (localStorage.getItem("role") !== "STUDENT") {
      studentsListApi();
    }
  }, [
    page,
    rowsPerPage,
    debouncedSearch,
    appliedCategory,
    appliedLevel,
    appliedTestType,
    appliedInstituteId,
    appliedStudentId,
    assessmentId,
  ]);

  console.log("category", category);

  useEffect(() => {
    // If navigated with category, level, testType, set them in both selected & applied states
    if (category || level || testType || institute_Name) {
      if (category) {
        setSelectedCategory(category);
        setAppliedCategory(category);
        localStorage.setItem("applyCategory", category);
      }
      if (level) {
        setSelectedLevel(level);
        setAppliedLevel(level);
        localStorage.setItem("applyLevel", level);
      }
      if (testType) {
        setSelectedTestType(testType);
        setAppliedTestType(testType);
        localStorage.setItem("applyTestType", testType);
      }
      // if (institute_Name) {
      //   setSelectedCollegeName(institute_Name);
      //   setAppliedInstituteName(institute_Name);
      //   // localStorage.setItem("applyInstitute", institute_Name);
      // }
    }
  }, [category, level, testType]);

  // Auto-apply student filter when navigated from QuestionPaperView
  useEffect(() => {
    if (studentid || studentName) {
      if (studentid) {
        setSelectedStudent(studentid);
        setAppliedStudentId(studentid);
        localStorage.setItem("applyStudentId", studentid);
      }
      if (studentName) {
        setSelectedStudentName(studentName);
        setAppliedStudentName(studentName);
        localStorage.setItem("applyStudent", studentName);
      }
    }
  }, [studentid, studentName]);

  const handleSearch = (e) => {
    navigate(location?.pathname, {
      replace: true,
      state: {
        studentid: null,
      },
    });
    const value = e.target.value;
    setIsViewAssessment(false);

    localStorage.setItem("searchvalue", value);
    setPage(0);
    setSearchKey(value);
  };

  const handleApplyCategoryLevelTestType = () => {
    tableCacheRef.current = {};
    setPage(0);

    setAppliedCategory(selectedCategory);
    localStorage.setItem("applyCategory", selectedCategory);

    setAppliedLevel(selectedLevel);
    localStorage.setItem("applyLevel", selectedLevel);

    setAppliedTestType(selectedTestType);
    localStorage.setItem("applyTestType", selectedTestType);

    setAppliedTestTypeId(selectedTestTypeId);
    localStorage.setItem("applyTestTypeId", selectedTestTypeId);

    setAppliedInstituteId(selectedCollege);
    setAppliedInstituteName(selectedCollegeName);
    localStorage.setItem("applyInstitute", selectedCollegeName);

    setAppliedStudentId(selectedStudent || null);
    setAppliedStudentName(selectedStudentName || "");
    localStorage.setItem("applyStudent", selectedStudentName || "");
    localStorage.setItem("applyStudentId", selectedStudent !== null && selectedStudent !== undefined ? selectedStudent : "");

    setOpen(false); // close drawer after applying
  };

  const handleClickClear = () => {
    tableCacheRef.current = {};
    setPage(0);
    // clear component states
    setAppliedCategory(null);
    setAppliedLevel(null);
    setSelectedCategory(null);
    setSelectedLevel(null);
    setSelectedTestType(null);
    setSelectedCollege(null);
    setSelectedCollegeName(null);
    setAppliedInstituteName(null);
    setAppliedInstituteId(null);

    setSelectedStudent(null);
    setSelectedStudentName(null);
    setAppliedStudentId(null);
    setAppliedStudentName(null);

    setSelectedTestType(null);
    setAppliedTestType(null);
    setSelectedTestTypeId(null);
    setAppliedTestTypeId(null);

    localStorage.removeItem("applyTestType");
    localStorage.removeItem("applyTestTypeId");

    // clear localStorage
    localStorage.removeItem("applyCategory");
    localStorage.removeItem("applyLevel");
    localStorage.removeItem("applyTestType");
    localStorage?.removeItem("applyInstitute");
    localStorage?.removeItem("applyStudent");
    localStorage?.removeItem("applyStudentId");

    // update location.state → set category, level, testType, assessmentId = null
    navigate(location.pathname, {
      replace: true,
      state: {
        ...location.state,
        category: null,
        level: null,
        testType: null,
        institute_id: null,
        institute_Name: null,
        testId: null,
        studentid: null,
        studentName: null,
        assessmentId: null,
      },
    });
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowTable(true);
    }, 200); // 0.2 sec delay

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    localStorage.setItem("currentPage", page);
    localStorage.setItem("rowsPerPage", rowsPerPage);
  }, [page, rowsPerPage]);


  return (
    <Box sx={{ minHeight: "100vh", p: 2 }}>
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            width: 320,
            p: 3,
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
            height: "100vh",
            borderRadius: "12px 0 0 12px",
          },
        }}
      >
        {/* Header */}
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <FilterListIcon color="primary" />
            <Typography variant="h6" fontWeight={600} color="primary">
              Filters
            </Typography>
          </Box>
          <IconButton aria-label="Close" size="small" onClick={() => setOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider />

        {/* Test Type */}
        <Box display="flex" flexDirection="column" gap={0.8}>
          <Typography variant="subtitle2" fontWeight={600}>
            Assessment Type
          </Typography>
          <Autocomplete
            size="small"
            options={testTypes}
            getOptionLabel={(option) => option?.test_type || ""}
            isOptionEqualToValue={(option, value) =>
              option.assessment_type_id === value?.assessment_type_id
            }
            value={
              testTypes.find(
                (item) => item.assessment_type_id === selectedTestTypeId
              ) || null
            }
            onChange={(event, newValue) => {
              setSelectedTestType(newValue ? newValue.test_type : null);
              setSelectedTestTypeId(
                newValue ? newValue.assessment_type_id : null
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select exam type"
                fullWidth
                variant="outlined"
              />
            )}
          />
        </Box>

        {localStorage.getItem("role") === "SUPER_ADMIN" && (
          <Box display="flex" flexDirection="column" gap={0.8}>
            <Typography variant="subtitle2" fontWeight={600}>
              Institute
            </Typography>

            <Autocomplete
              size="small"
              options={colleges}
              getOptionLabel={(option) => option?.name || ""}
              value={
                colleges.find(
                  (college) => college.institute_id === selectedCollege
                ) || null
              }
              onChange={(event, newValue) => {
                localStorage.setItem(
                  "Institute_Id_Filter",
                  newValue ? newValue.institute_id : ""
                );

                setPage(0);
                setSelectedCollege(newValue ? newValue.institute_id : "");
                setSelectedCollegeName(newValue ? newValue.name : "");
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="All Institutes"
                  size="small"
                  variant="outlined"
                  fullWidth
                />
              )}
            />
          </Box>
        )}

        {/* Student */}
        <Box display="flex" flexDirection="column" gap={0.8}>
          <Typography variant="subtitle2" fontWeight={600}>
            {getLabel('STUDENT')}
          </Typography>
          <Autocomplete
            size="small"
            options={studentsList}
            value={
              studentsList.find(
                (student) => student.student_id === selectedStudent
              ) || null
            }
            onChange={(event, newValue, reason) => {
              setSelectedStudent(newValue ? newValue.student_id : null);
              setSelectedStudentName(newValue ? newValue.student_name : "");

              // Clicking the "x" clears the student filter → immediately refetch
              // ExamHistoryDetails with student_id = null.
              if (reason === "clear" || !newValue) {
                tableCacheRef.current = {};
                setPage(0);
                setAppliedStudentId(null);
                setAppliedStudentName("");
                localStorage.removeItem("applyStudent");
                localStorage.removeItem("applyStudentId");
                // Drop any studentid carried in navigation state so it can't
                // override the null student_id in the fetch payload.
                navigate(location.pathname, {
                  replace: true,
                  state: {
                    ...location.state,
                    studentid: null,
                    studentName: null,
                  },
                });
              }
            }}
            getOptionLabel={(option) => option?.student_name || ""}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={`Select ${getLabel('STUDENT')}`}
                variant="outlined"
                size="small"
                fullWidth
              />
            )}
          />
        </Box>

        {/* Category */}
        <Box display="flex" flexDirection="column" gap={0.8}>
          <Typography variant="subtitle2" fontWeight={600}>
            {getLabel('SUBJECT')}
          </Typography>
          <Autocomplete
            size="small"
            options={categories}
            value={selectedCategory}
            onChange={(event, newValue) => setSelectedCategory(newValue)}
            getOptionLabel={(option) => option || ""}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={`Select ${getLabel('SUBJECT')}`}
                variant="outlined"
                size="small"
                fullWidth
              />
            )}
          />
        </Box>

        {/* Level */}
        <Box display="flex" flexDirection="column" gap={0.8}>
          <Typography variant="subtitle2" fontWeight={600}>
            Level
          </Typography>
          <Autocomplete
            size="small"
            options={levelOptions}
            value={selectedLevel}
            onChange={(event, newValue) => setSelectedLevel(newValue)}
            getOptionLabel={(option) => option || ""}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select Level"
                variant="outlined"
                size="small"
                fullWidth
              />
            )}
          />
        </Box>

        {/* <Divider sx={{ mt: 2 }} /> */}

        {/* Apply Button */}
        <Button
          variant="contained"
          color="primary"
          fullWidth
          className="customButton"
          onClick={handleApplyCategoryLevelTestType}
        >
          Apply Filters
        </Button>
      </Drawer>

      {/* Header */}
      <Grid
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: 2,
          // borderBottom: "1px solid #828487",
          pb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box className="icon-box">
            <HistoryIcon className="iconHeading" />
          </Box>
          <Typography sx={{ fontSize: { xs: 20, sm: 28 }, fontWeight: 700, color: "#0a0a0a" }}>
            Assessment History
          </Typography>
        </Box>

        {localStorage?.getItem("role") !== "STUDENT" && (
          <TextField
            value={searchKey}
            onChange={handleSearch}
            size="small"
            variant="outlined"
            placeholder={`Search ${getLabel('STUDENT')}`}
            className="custom-search-field"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        )}
      </Grid>

      {/* Table */}
      <TableContainer
        component={Paper}
        sx={{
          opacity: showTable ? 1 : 0,
          transform: showTable ? "translateY(0)" : "translateY(40px)", // start from bottom
          transition: "opacity 0.5s ease, transform 0.5s ease",
        }}
      >
        <Table>
          <TableHead>
            {/* FILTER ROW */}
            <TableRow>
              <TableCell colSpan={headers.length}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  {/* LEFT — FILTER CHIPS */}
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {appliedTestType && appliedTestType !== "null" && (
                      <Box className="filter-chip">
                        <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
                          {appliedTestType ||
                            localStorage.getItem("applyTestType")}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setAppliedTestType(null);
                            setSelectedTestType(null);
                            setSelectedTestTypeId(null);
                            setAppliedTestTypeId(null);
                            setPage(0);
                            localStorage.removeItem("applyTestType");
                            localStorage.removeItem("applyTestTypeId");
                            navigate(location.pathname, {
                              replace: true,
                              state: {
                                ...location.state,
                                testType: null,
                                testId: null,
                              },
                            });
                          }}
                          sx={{ ml: 0.5 }}
                        >
                          <CloseIcon sx={{ fontSize: "14px" }} />
                        </IconButton>
                      </Box>
                    )}

                    {localStorage?.getItem("role") === "SUPER_ADMIN" &&
                      appliedInstituteName &&
                      appliedInstituteName !== "null" && (
                        <Box className="filter-chip">
                          <Typography
                            sx={{ fontSize: "13px", fontWeight: 500 }}
                          >
                            {appliedInstituteName ||
                              localStorage.getItem("applyInstitute")}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setAppliedInstituteName(null);
                              setSelectedCollegeName(null);
                              setSelectedCollege(null);
                              setAppliedInstituteId(null);
                              setPage(0);
                              localStorage.removeItem("applyInstitute");
                              navigate(location.pathname, {
                                replace: true,
                                state: {
                                  ...location.state,
                                  institute_Name: null,
                                  institute_id: null,
                                },
                              });
                            }}
                            sx={{ ml: 0.5 }}
                          >
                            <CloseIcon sx={{ fontSize: "14px" }} />
                          </IconButton>
                        </Box>
                      )}

                    {appliedCategory && appliedCategory !== "null" && (
                      <Box className="filter-chip">
                        <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
                          {appliedCategory}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setAppliedCategory(null);
                            setSelectedCategory(null);
                            setPage(0);
                            localStorage.removeItem("applyCategory");
                            navigate(location.pathname, {
                              replace: true,
                              state: {
                                ...location.state,
                                category: null,
                                assessmentId: null,
                              },
                            });
                          }}
                          sx={{ ml: 0.5 }}
                        >
                          <CloseIcon sx={{ fontSize: "14px" }} />
                        </IconButton>
                      </Box>
                    )}

                    {appliedLevel && appliedLevel !== "null" && (
                      <Box className="filter-chip">
                        <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
                          {appliedLevel || localStorage.getItem("applyLevel")}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setAppliedLevel(null);
                            setSelectedLevel(null);
                            setPage(0);
                            localStorage.removeItem("applyLevel");
                            navigate(location.pathname, {
                              replace: true,
                              state: { ...location.state, level: null },
                            });
                          }}
                          sx={{ ml: 0.5 }}
                        >
                          <CloseIcon sx={{ fontSize: "14px" }} />
                        </IconButton>
                      </Box>
                    )}

                    {appliedStudentName && appliedStudentName !== "null" && appliedStudentName !== "" && (
                      <Box className="filter-chip">
                        <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
                          {appliedStudentName}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setAppliedStudentId(null);
                            setAppliedStudentName(null);
                            setSelectedStudent(null);
                            setSelectedStudentName(null);
                            setPage(0);
                            localStorage.removeItem("applyStudent");
                            localStorage.removeItem("applyStudentId");
                          }}
                          sx={{ ml: 0.5 }}
                        >
                          <CloseIcon sx={{ fontSize: "14px" }} />
                        </IconButton>
                      </Box>
                    )}
                  </Box>

                  {/* RIGHT — MORE FILTER BUTTON */}
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <IconButton
                      onClick={() => setOpen(true)}
                      color="primary"
                      className="more-filter-btn"
                    >
                      <FilterListIcon />
                      <Typography className="more-filter-text">
                        More filter
                      </Typography>
                    </IconButton>

                    {(appliedCategory !== null && appliedCategory !== "null" ||
                      appliedLevel !== null && appliedLevel !== "null" ||
                      appliedTestType !== null && appliedTestType !== "null" ||
                      appliedStudentName !== null && appliedStudentName !== "" ||
                      (localStorage?.getItem("role") === "SUPER_ADMIN" &&
                        appliedInstituteName !== null && appliedInstituteName !== "")) && (
                        <Button
                          variant="outlined"
                          className="clear-filter-btn"
                          onClick={handleClickClear}
                        >
                          Clear Filter
                        </Button>
                      )}
                  </Box>
                </Box>
              </TableCell>
            </TableRow>

            {/* TABLE HEADER */}
            <TableRow>
              {headers.map((head, i) => (
                <TableCell key={i} align={head.align} className="tableHeader">
                  <b>{head.label}</b>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          {/* TABLE BODY */}
          {loading ? (
            <TableCell colSpan={headers.length} align="center">
              <TableLoader message="Fetching results..." />
            </TableCell>
          ) : (
            <TableBody className="tablebody">
              {listData.map((item, index) => (
                <TableRow
                  key={index}
                  hover
                  sx={{
                    backgroundColor: index % 2 === 1 ? "#FAFAFA" : "inherit",
                    "&:hover": { backgroundColor: "#f1f1f1" },
                  }}
                >
                  <TableCell align="center">
                    {page * rowsPerPage + index + 1}
                  </TableCell>
                  <TableCell>{item.name}</TableCell>
                  {localStorage?.getItem("role") === "SUPER_ADMIN" && (
                    <TableCell>{item.short_name}</TableCell>
                  )}
                  <TableCell sx={{ maxWidth: '200px' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                      {(() => {
                        const parts = item.category?.split('-') || [];
                        if (parts.length >= 3) {
                          const firstPart = parts.slice(0, 2).join('-');
                          const secondPart = parts.slice(2).join('-');
                          return (
                            <>
                              <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#1a1a1a', wordBreak: 'break-word' }}>
                                {firstPart}
                              </Typography>
                              <Typography sx={{ fontSize: '12px', color: '#666', wordBreak: 'break-word' }}>
                                {secondPart}
                              </Typography>
                            </>
                          );
                        }
                        return (
                          <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#1a1a1a', wordBreak: 'break-word' }}>
                            {item.category}
                          </Typography>
                        );
                      })()}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ minWidth: '140px' }}>
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.6,
                      p: 0.8,
                      backgroundColor: '#f8fafc',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0'
                    }}>
                      <Chip
                        label={item.test_type}
                        size="small"
                        color={
                          item.test_type === "Proctored Assessment"
                            ? "secondary"
                            : "primary"
                        }
                        variant="filled"
                        sx={{
                          fontWeight: 600,
                          fontSize: '11px',
                          color: "white",
                          height: '24px',
                          borderRadius: '4px',
                          justifyContent: 'center'
                        }}
                      />
                      {item.level && (
                        <Chip
                          label={item.level}
                          size="small"
                          variant="outlined"
                          sx={{
                            fontWeight: 500,
                            fontSize: '11px',
                            height: '22px',
                            borderRadius: '4px',
                            color: '#64748b',
                            borderColor: '#cbd5e1',
                            backgroundColor: 'white',
                            justifyContent: 'center'
                          }}
                        />
                      )}
                      <Chip
                        label={`ASS - ${item.assessment_id}`}
                        size="small"
                        variant="outlined"
                        sx={{
                          fontWeight: 600,
                          fontSize: '11px',
                          height: '22px',
                          borderRadius: '4px',
                          color: '#475569',
                          borderColor: '#cbd5e1',
                          backgroundColor: 'white',
                          justifyContent: 'center'
                        }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={`${Number((item.raw_total || 0).toFixed(2))} / ${item.max_total ?? 0}`}
                      sx={{
                        fontWeight: 700,
                        color: "white",
                        px: 1,
                        py: 0.75,
                        borderRadius: "10px",
                        background: "linear-gradient(90deg, #1976d2, #42a5f5)",
                        fontSize: "0.875rem",
                        letterSpacing: "0.5px",
                        // boxShadow: "0 4px 12px rgba(25, 118, 210, 0.25)",
                        transition:
                          "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                        position: "relative",
                        overflow: "hidden",
                        "& .MuiChip-label": {
                          px: 0.5,
                          position: "relative",
                          zIndex: 1,
                        },
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          top: 0,
                          left: "-100%",
                          width: "100%",
                          height: "100%",
                          background:
                            "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
                          transition: "left 0.6s ease",
                        },
                        "&:hover": {
                          transform: "translateY(-2px) scale(1.05)",
                          boxShadow: "0 8px 25px rgba(25, 118, 210, 0.4)",
                          "&::before": {
                            left: "100%",
                          },
                        },
                      }}
                    />
                  </TableCell>

                  <TableCell>
                    {dayjs(item.submitted_at).format("DD-MM-YYYY hh:mm A")}
                  </TableCell>

                  <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                    <IconButton
                      color="primary"
                      onClick={() =>
                        navigate("/examOverview", {
                          state: {
                            assessmentId: item?.assessment_id,
                            attemptId: item.attempt_id,
                            studentId: item.student_user_id,
                            studentName: item.name,
                          },
                        })
                      }
                    >
                      <ManageSearchOutlinedIcon />
                      <Typography
                        color="primary"
                        sx={{ fontSize: "12px", ml: "4px" }}
                      >
                        Assessment Overview
                      </Typography>
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}

              {((page === 0 && listData?.length > rowsPerPage - 1) ||
                page !== 0) && (
                  <CustomPagination
                    page={page}
                    setPage={setPage}
                    count={count}
                    rowsPerPage={rowsPerPage}
                    colSpan={
                      localStorage.getItem("role") === "SUPER_ADMIN" ? 8 : 7
                    }
                  />
                )}

              {listData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={headers.length} align="center">
                    <TableNodata />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          )}
        </Table>
      </TableContainer>
    </Box>
  );
};

export default StudentsResultList;
